import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { comboLabel, parseComboKey } from '../routing';

const ROLE_COLORS = {
  ADMIN: 'bg-purple-100 text-purple-800 border-purple-200',
  COORDINATOR: 'bg-blue-100 text-blue-800 border-blue-200',
  USER: 'bg-slate-100 text-slate-800 border-slate-200',
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-red-100 text-red-800',
};

const UserTable = ({
  users,
  currentUserId,
  loading,
  onEdit,
  onDelete,
  onResetPassword,
  pagination,
  onPageChange,
  routingByUser = {},
  onShowRouting,
}) => {
  if (loading && users.length > 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Users" size={48} className="mx-auto text-slate-300 mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-1">No users found</h3>
        <p className="text-slate-500">Try adjusting your search or filter criteria</p>
      </div>
    );
  }

  return (
    <div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Approves
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {users.map((user) => (
              <tr 
                key={user.id} 
                className={`hover:bg-slate-50 transition-colors ${
                  user.id === currentUserId ? 'bg-blue-50/50' : ''
                }`}
              >
                {/* User Info */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center">
                        <span className="text-slate-600 font-medium">
                          {user.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                        {user.full_name || 'Unnamed User'}
                        {user.id === currentUserId && (
                          <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                            You
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-500">{user.email}</div>
                    </div>
                  </div>
                </td>

                {/* Role */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${ROLE_COLORS[user.role] || ROLE_COLORS.USER}`}>
                    <Icon 
                      name={user.role === 'ADMIN' ? 'Shield' : user.role === 'COORDINATOR' ? 'UserCog' : 'User'} 
                      size={12} 
                      className="mr-1" 
                    />
                    {user.role}
                  </span>
                </td>

                {/* Approves: the invite / quality combinations this user is named in */}
                <td className="px-6 py-4 whitespace-nowrap" data-testid={`user-approves-${user.id}`}>
                  {(() => {
                    const mine = routingByUser[user.id] || { INVITE: [], QUALITY: [] };
                    if (!mine.INVITE.length && !mine.QUALITY.length) return <span className="text-sm text-slate-400">—</span>;
                    const chip = (kind, keys, label, cls) =>
                      keys.length > 0 && (
                        <button
                          type="button"
                          onClick={() => onShowRouting?.(kind)}
                          title={keys.map((k) => { const c = parseComboKey(k); return comboLabel(c.nature_of_business, c.supplier_subcategory); }).join('\n')}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium hover:shadow-sm ${cls}`}
                        >
                          {label} · {keys.length}
                        </button>
                      );
                    return (
                      <div className="flex flex-wrap gap-1.5">
                        {chip('INVITE', mine.INVITE, 'Invites', 'bg-sky-50 text-sky-800 border-sky-200')}
                        {chip('QUALITY', mine.QUALITY, 'Quality', 'bg-violet-50 text-violet-800 border-violet-200')}
                      </div>
                    );
                  })()}
                </td>

                {/* Status */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.is_active ? STATUS_COLORS.active : STATUS_COLORS.inactive
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                      user.is_active ? 'bg-green-500' : 'bg-red-500'
                    }`}></span>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>

                {/* Created */}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                  {user.created_at 
                    ? new Date(user.created_at).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'N/A'}
                </td>

                {/* Last Login */}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                  {user.last_login 
                    ? new Date(user.last_login).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : 'Never'}
                </td>

                {/* Actions */}
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEdit(user)}
                      className="text-slate-600 hover:text-blue-600 transition-colors p-1 rounded hover:bg-blue-50"
                      title="Edit user"
                    >
                      <Icon name="Pencil" size={16} />
                    </button>
                    <button
                      onClick={() => onResetPassword(user)}
                      className="text-slate-600 hover:text-amber-600 transition-colors p-1 rounded hover:bg-amber-50"
                      title="Reset password"
                    >
                      <Icon name="Key" size={16} />
                    </button>
                    {user.id !== currentUserId && (
                      <button
                        onClick={() => onDelete(user.id)}
                        className="text-slate-600 hover:text-red-600 transition-colors p-1 rounded hover:bg-red-50"
                        title={user.is_active ? 'Deactivate user' : 'User already inactive'}
                        disabled={!user.is_active}
                      >
                        <Icon name={user.is_active ? 'UserMinus' : 'UserX'} size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
          <div className="text-sm text-slate-600">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              <Icon name="ChevronLeft" size={16} />
            </Button>
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <Button
                  key={page}
                  variant={pagination.page === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => onPageChange(page)}
                  className="min-w-[36px]"
                >
                  {page}
                </Button>
              );
            })}
            {pagination.totalPages > 5 && <span className="text-slate-400">...</span>}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
            >
              <Icon name="ChevronRight" size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTable;







